import { ApplyForm } from 'api/U002';
import { Types } from 'commons/ui-generator/ContentsHandler';
import { useCallback, useEffect, useState, useContext } from 'react';
import { FieldValues, UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { MembersAnswersInput } from './useAuditionForm';
import { getAnswers } from 'commons/auditionForm/autosave/utils';
import { AppContext } from 'templates/AppContextProvider';

const INITIAL_NUM = [crypto.randomUUID(), crypto.randomUUID()];

const isCompareEqual = (a: string[], b: string[]) => {
  return JSON.stringify(a) === JSON.stringify(b);
};

// ContentsHandlerのContextで使用する値
export const useTemplateMembersNum = (
  auditionId: string,
  examinationNo: number,
  setValue: UseFormSetValue<FieldValues>,
  getValues: UseFormGetValues<FieldValues>,
  applyForm?: ApplyForm[]
): Pick<
  Types.ContentsHandlerContextValue,
  | 'applyType'
  | 'templateMembersIds'
  | 'setTemplateMembersIds'
  | 'onChangeApplyType'
  | 'onChangeTemplateMembersNum'
  | 'onClickTemplateMembersDelete'
> => {
  const { isGlobal } = useContext(AppContext);
  // AutoSaveがあった場合は、そのメンバー数,応募方法を初期値にする
  // それ以外は2, GROUP
  useEffect(() => {
    console.log('useTemplateMembersNum: useEffect');
    const initialApplyType = (() => {
      try {
        const membersApplyFormNo = applyForm?.find((form) => form.format === '54')?.apply_form_no;
        if (!membersApplyFormNo) return 'GROUP';
        const answers = getAnswers(auditionId, examinationNo);
        const membersAnswer = answers?.find(
          (answer) => answer.apply_form_no === `${membersApplyFormNo}`
        );
        if (!membersAnswer) return 'GROUP';
        const type = (membersAnswer.answer as string).split('\n')[0];
        return type.includes('ソロ') || type.includes('Solo') ? 'SOLO' : 'GROUP';
      } catch (e) {
        console.error('Error parsing localStorage data', e);
        return 'GROUP';
      }
    })();
    setApplyType(initialApplyType);

    const initialNum = (() => {
      try {
        const membersApplyFormNo = applyForm?.find((form) => form.format === '54')?.apply_form_no;
        if (!membersApplyFormNo) return INITIAL_NUM;
        const answers = getAnswers(auditionId, examinationNo);
        const membersAnswer = answers?.find(
          (answer) => answer.apply_form_no === `${membersApplyFormNo}`
        );
        if (!membersAnswer) return INITIAL_NUM;
        const num = parseInt(
          (membersAnswer.answer as string)
            .split('\n')[0]
            .substring(
              (membersAnswer.answer as string).split('\n')[0].indexOf(' ') + 1,
              (membersAnswer.answer as string).split('\n')[0].indexOf('名')
            )
        );
        return num > 1 ? new Array(num).fill(null).map(() => crypto.randomUUID()) : INITIAL_NUM;
      } catch (e) {
        console.error('Error parsing localStorage data', e);
        return INITIAL_NUM;
      }
    })();
    setIds((ids) => {
      // 初期値と違う場合は何もしない
      if (!isCompareEqual(ids, INITIAL_NUM)) return ids;
      return initialNum;
    });
  }, [auditionId, examinationNo, applyForm]);

  const [ids, setIds] = useState<string[]>(INITIAL_NUM);
  const [applyType, setApplyType] = useState<'SOLO' | 'GROUP'>('GROUP');

  const onChangeApplyType = useCallback<Types.ContentsHandlerContextValue['onChangeApplyType']>(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const type = e.currentTarget.value;
      if (type === 'ソロ' || type === 'Solo') {
        const targetApplyFormNo = applyForm?.find((form) => form.format === '54')?.apply_form_no;
        const soloValue = {
          apply_type: isGlobal ? 'Solo' : 'ソロ',
          member_num: '2',
          consent: false,
        };
        setValue(`${targetApplyFormNo}` as const, [soloValue]);
        setApplyType('SOLO');
      } else {
        setApplyType((prev) => {
          if (prev === 'SOLO') setIds(INITIAL_NUM);
          return 'GROUP';
        });
      }
    },
    [applyType, setApplyType, applyForm, setValue]
  );

  const onChangeTemplateMembersNum = useCallback<
    Types.ContentsHandlerContextValue['onChangeTemplateMembersNum']
  >(
    (inputedMemberNum: number) => {
      const prevMembers = ids.length;
      // input > prevの場合 差分だけ後ろに追加
      if (inputedMemberNum > prevMembers) {
        const diff = inputedMemberNum - prevMembers;
        const addIds: string[] = [];
        for (var i = 0; i < diff; i++) {
          addIds.push(crypto.randomUUID());
        }
        setIds((ids) => [...ids, ...addIds]);
      }
      // input < prevの場合 差分だけ後ろから削除
      if (inputedMemberNum < prevMembers) {
        const diff = prevMembers - inputedMemberNum;
        const cut = Math.max(0, ids.length - diff);
        const cutted = ids.slice(cut);
        cutted.forEach((id) => onClickTemplateMembersDelete(id));
      }
    },
    [ids, setIds]
  );
  const onClickTemplateMembersDelete = useCallback<
    Types.ContentsHandlerContextValue['onClickTemplateMembersDelete']
  >(
    (targetId) => {
      const targetApplyFormNo = applyForm?.find((form) => form.format === '54')?.apply_form_no;
      if (targetApplyFormNo == null) return;
      const targetIndex = ids.findIndex((id) => id === targetId);
      if (targetIndex === -1) return ids;
      setIds((ids) => {
        return ids.filter((id) => id !== targetId);
      });
      const membersValue = getValues(`${targetApplyFormNo}` as const) as MembersAnswersInput;
      const removedValue = membersValue?.toSpliced(targetIndex, 1);
      setValue(`${targetApplyFormNo}` as const, removedValue);
    },
    [ids, setIds, applyForm, setValue, getValues]
  );

  return {
    applyType: applyType,
    templateMembersIds: ids,
    setTemplateMembersIds: setIds,
    onChangeApplyType,
    onChangeTemplateMembersNum,
    onClickTemplateMembersDelete,
  };
};
